import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { OPPORTUNITY_LOST_REASONS, type ActorView, type TimelineItem as Item } from "@gioia365/contracts";
import { messageKeysUnder, type Locale, type MessageKey } from "@gioia365/i18n";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Icon, type IconName } from "../../design/components/Icon";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatClockDuration, formatEuroCents, formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { stageText } from "../../appointments/appointment-texts";
import { lostReasonText } from "../../opportunities/opportunity-texts";
import { skipReasonText } from "../../dialer/dialer-texts";
import { callQualityLabel, ringDurationText, outcomeText, RECORDING_LINES, recordingLineOf, type RecordingLine } from "../../telephony/call-display";
import { RecordingPlayer } from "../../telephony/RecordingPlayer";
import { useMarkWhatsAppRead } from "../../conversations/whatsapp-queries";
import { alreadyDeclared, useDeclareWhatsAppSent } from "../../whatsapp/api";
import { inboundKindKey } from "../../whatsapp/inbound-kinds";
import { agileFailureText, agileStateKey } from "../../whatsapp/whatsapp-texts";
import { formatEuro, formatNumber } from "../card/card-fields";
import { sequenceEventTitle } from "../card/sequence-texts";
import { formatPhoneForDisplay } from "../lead-display";
import { useLeadView } from "../lead-queries";
import { callAnchorId, focusStayedIn, timelineAnchorId } from "./focus";
import { NoteAttachments } from "./NoteAttachments";
import { NoteComposer } from "./NoteComposer";

// Un elemento della Timeline (piano M2 Task 19, mock-up `scheda-lead.html`): l'icona del tipo, il titolo, «quando · chi»
// con «da Close» per ciò che viene da Close (proposta 17 dell'importazione), e il dettaglio. La telefonata ha i suoi
// appunti e «Aggiungi appunto»; un salvataggio di più campi è una riga che si apre e mostra ogni campo «da … a …»; lo
// storico di Close (opportunità, task, riunioni, attività personalizzate) ha i suoi dettagli leggibili. Niente si
// modifica né si cancella: la Timeline è di sola aggiunta. La telefonata della telefonia (piano M4, Task 12) dice «In corso»
// finché non è finita, l'esito in due passi («Risposta · Richiamare»), lo stato della registrazione con le parole di
// `REC_TEXT` del mock-up e, a chi la può ascoltare, «Ascolta». La sequenza di chiamate (piano M5, Task 12): la telefonata dice
// il suo posto in fondo a «quando · chi» («tentativo 1 · chiamata 2») e ha l'`id` a cui porta «Vedi nella Timeline»; gli eventi
// della sequenza hanno i titoli di D16 (`sequence-texts.ts`). I richiami (piano M5, Task 17; D18): «Richiamo fissato per …»
// (con «concordato con il Lead»), «Richiamo spostato da … a …» dal prima e dal dopo dell'evento, «Richiamo fatto» e «Richiamo
// annullato», con le ore nel fuso dell'azienda. La telefonata registrata con «Registra attività» (piano M5, Task 19) dice in
// «quando · chi» i minuti di conversazione e «inserita a mano» (`callMeta`). Un WhatsApp (piano M6, R1, Task 4a; D3, questione 402) è
// un elemento per messaggio con lo stato di adesso, invece delle due righe del mock-up: il titolo dallo stato e dal modello, in
// «quando · chi» l'ora della preparazione, il numero e, dichiarato inviato, l'ora della dichiarazione con «consegna e lettura non
// verificabili»; sotto, il testo esatto che il Lead ha ricevuto. All'autore di un messaggio soltanto preparato offre «Sì, l'ho
// inviato» (`canDeclareSent`), come la finestra. Dal WhatsApp ufficiale (piano M6, R2, Task 10a) il titolo dice il canale e lo
// stato che dice Meta («WhatsApp ufficiale · Preso in carico da Meta», «Inviato», «Consegnato», «Letto dal Lead», «Non consegnato»
// o «Non confermato»), e un messaggio non consegnato ha in «quando · chi» il motivo. Un messaggio del Lead (piano M6, R3, passo 11a)
// dice «Ha scritto il Lead» con «Da leggere» finché nessuno l'ha letto, in «quando · chi» il numero dell'azienda che l'ha ricevuto e,
// sotto, il testo; per un'immagine, un audio e gli altri tipi la frase che dice di aprirli sul telefono, con la didascalia. Letto
// (passo 12b, questione 423), «quando · chi» dice da chi e quando («Letto da Luca Bianchi il …», «Letto da Gioia365 il …» per una
// lettura dal contesto di Gioia365); da leggere, a chi lo può segnare letto (`canMarkRead`) offre «Segna come letto». Aprire la scheda
// non lo segna letto: chi guarda come un collega, o apre la scheda per altro, toglierebbe il segnale a chi deve rispondere. Un evento
// delle email di GoHighLevel (ingresso dei Lead, I5, D5) è un elemento in sola lettura: il titolo dall'evento («Clic su un link
// dell'email»), in «quando · chi» GoHighLevel, sotto l'oggetto e il link (che si apre in una scheda nuova), se il flusso li manda.
// Una nota di Close ha sotto i suoi allegati, con lo stato della copia e «Apri» o «Scarica» (INGR-27, `NoteAttachments.tsx`).

type CallItem = Extract<Item, { kind: "call" }>;
type NoteItem = Extract<Item, { kind: "note" }>;
type ChangeItem = Extract<Item, { kind: "change" }>;
export type EventItem = Extract<Item, { kind: "event" }>;
type WhatsAppItem = Extract<Item, { kind: "whatsapp" }>;
type EmailItem = Extract<Item, { kind: "email" }>;
export type Change = ChangeItem["changes"][number];

export type TimelineItemProps = {
  item: Item;
  /** La scheda, con cui «Ascolta» chiede l'indirizzo della registrazione. */
  tab: TabContext;
  timeZone: string;
  /** Il tipo dei campi dell'azienda dal modello della scheda (`field:<chiave>` → `currency_eur`, `number`, …). */
  fieldTypes: ReadonlyMap<string, string>;
  canAddNotes: boolean;
  onAddMemo: (callId: string, body: string) => Promise<unknown>;
};

export function TimelineItem(props: TimelineItemProps) {
  const { item } = props;
  switch (item.kind) {
    case "call":
      return <CallEntry {...props} item={item} />;
    case "note":
      return <NoteEntry item={item} tab={props.tab} timeZone={props.timeZone} />;
    case "change":
      return <ChangeEntry item={item} timeZone={props.timeZone} fieldTypes={props.fieldTypes} />;
    case "event":
      return <EventEntry item={item} timeZone={props.timeZone} />;
    case "whatsapp":
      return <WhatsAppEntry item={item} tab={props.tab} timeZone={props.timeZone} />;
    case "email":
      return <EmailEntry item={item} timeZone={props.timeZone} />;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Le parti comuni

/** Il nome di chi ha fatto la cosa, come allora. */
function actorName(t: Translate, actor: ActorView): string {
  return actor.displayName.trim() || t("timeline.unknownAuthor");
}

/** Il segno «da Close», accanto a «quando · chi». */
function FromClose({ show }: { show: boolean }) {
  const t = useT();
  return show ? <Chip tone="accent">{t("timeline.fromClose")}</Chip> : null;
}

type MetaProps = { at: string; known?: boolean; timeZone: string; parts: Array<string | null>; fromClose: boolean; badge?: ReactNode };

/**
 * «24/09/2026 08:37 · durata 0:12 · Luca Bianchi», nel fuso dell'azienda; «data originale non nota» se Close non la sa. `badge` è un
 * segno dopo «quando · chi» («Da leggere» di un WhatsApp del Lead).
 */
function Meta({ at, known = true, timeZone, parts, fromClose, badge }: MetaProps) {
  const t = useT();
  const rest = parts
    .filter((part): part is string => part !== null && part !== "")
    .map((part) => ` · ${part}`)
    .join("");
  return (
    <p className="g-timeline-item__meta">
      <span>
        {known ? <time dateTime={at}>{formatNumericDateTime(timeZone, at)}</time> : <span className="g-timeline-item__unknown-date">{t("timeline.dateUnknown")}</span>}
        {rest}
      </span>
      <FromClose show={fromClose} />
      {badge}
    </p>
  );
}

/**
 * Un elemento; con `anchor` ha un `id` e prende il focus quando «Vedi nella Timeline» (la telefonata, dalla sequenza) o un link di
 * «Attività» (ogni elemento, SEZ-8) lo porta in vista.
 */
function Entry({ kind, icon, anchor, children }: { kind: string; icon: IconName; anchor?: string; children: ReactNode }) {
  return (
    <li className="g-timeline-item" data-kind={kind} id={anchor} tabIndex={anchor === undefined ? undefined : -1}>
      <span className="g-timeline-item__icon">
        <Icon name={icon} />
      </span>
      <div className="g-timeline-item__content">{children}</div>
    </li>
  );
}

/** La prima lettera maiuscola: una riga «da … a …» che diventa frase. */
function sentence(locale: Locale, text: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

// ---------------------------------------------------------------------------------------------------------------
// Telefonata

/** Una telefonata della telefonia ancora in corso: né la validità, né l'esito, né la durata sono quelli della fine. */
const isLive = (item: CallItem) => item.liveState !== undefined && item.liveState !== "ended";

function callTitle(t: Translate, item: CallItem): string {
  const direction = item.direction === "inbound" ? t("timeline.call.inbound") : t("timeline.call.outbound");
  let outcome: string;
  if (isLive(item)) outcome = t("timeline.call.live");
  else if (item.quality?.numberProblem && item.outcome === "wrong_number") outcome = outcomeText(t, item.outcome, item.answeredOutcome, item.outcomeDetail);
  else if (callQualityLabel(t, item.quality)) outcome = callQualityLabel(t, item.quality)!;
  else if (!item.valid) outcome = t("timeline.call.failed");
  else if (item.outcome === null) outcome = t("timeline.call.unknownOutcome");
  else outcome = outcomeText(t, item.outcome, item.answeredOutcome, item.outcomeDetail);
  return `${direction} · ${outcome}`;
}

/**
 * La riga della registrazione di una telefonata, oppure nessuna (`call-display.ts`, la stessa di «Conversazioni»): una risposta
 * di prima del Task 12 non dice niente, una chiamata mai arrivata al Lead non conta.
 */
function recordingLineOfItem(item: CallItem): RecordingLine | null {
  if (item.recording === undefined) return null;
  if (!isLive(item) && !item.valid) return null;
  return recordingLineOf({ live: isLive(item), recording: item.recording, canListen: item.canListen === true, origin: item.source ?? "telephony" });
}

/**
 * Ciò che segue l'ora di una telefonata in «quando · chi»: la durata (per una telefonata inserita a mano i minuti di conversazione e
 * «inserita a mano», piano M5, Task 19), chi l'ha fatta e il suo posto nella sequenza.
 */
function callMeta(t: Translate, item: CallItem): Array<string | null> {
  const manual = item.source === "manual";
  let duration: string | null = null;
  if (item.durationSeconds !== null && !isLive(item)) {
    duration = manual ? t("timeline.call.minutes", { count: Math.round(item.durationSeconds / 60) }) : t("timeline.call.duration", { duration: formatClockDuration(item.durationSeconds) });
  }
  return [
    duration,
    ringDurationText(t, item.quality),
    manual ? t("timeline.call.manual") : null,
    actorName(t, item.actor),
    item.sequenceSlot ? t("timeline.event.callSlot", { attempt: item.sequenceSlot.attempt, number: item.sequenceSlot.callNumber }) : null,
  ];
}

function CallEntry({ item, tab, timeZone, canAddNotes, onAddMemo }: TimelineItemProps & { item: CallItem }) {
  const t = useT();
  const toast = useToast();
  const [writing, setWriting] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);
  const focusBack = useRef(false);

  // Salvato o annullato l'appunto, il focus torna su «Aggiungi appunto» di questa telefonata, come in `FieldEditor`: il
  // riquadro chiuso lo lascia sulla pagina. Se intanto la persona scrive altrove, resta dov'è.
  useEffect(() => {
    if (writing || !focusBack.current) return;
    focusBack.current = false;
    if (focusStayedIn(null)) addRef.current?.focus();
  }, [writing]);

  function closeMemo() {
    focusBack.current = true;
    setWriting(false);
  }

  async function saveMemo(body: string) {
    await onAddMemo(item.id, body);
    closeMemo();
    toast.show(t("timeline.memo.saved"), "success");
  }

  const line = recordingLineOfItem(item);
  const lineView = line === null ? null : RECORDING_LINES[line];
  const player =
    line === "available" ? <RecordingPlayer tab={tab} callId={item.id} durationSeconds={item.recording?.durationSeconds ?? item.durationSeconds} /> : null;

  return (
    <Entry kind="call" icon="phone" anchor={callAnchorId(item.id)}>
      <p className="g-timeline-item__title">{callTitle(t, item)}</p>
      <Meta at={item.occurredAt} timeZone={timeZone} parts={callMeta(t, item)} fromClose={item.fromClose} />
      {lineView ? (
        <p className="g-recording-state">
          <StatusPill tone={lineView.tone} icon={lineView.icon}>
            {t(lineView.text)}
          </StatusPill>
        </p>
      ) : null}
      {item.notes.map((memo) => {
        const when = ` · ${formatNumericDateTime(timeZone, memo.occurredAt)} · ${actorName(t, memo.actor)}`;
        return (
          <div key={memo.id} className="g-timeline-item__memo">
            <p className="g-timeline-item__memo-meta">
              <span>
                <strong>{t("timeline.memo.title")}</strong>
                {when}
              </span>
              <FromClose show={memo.actor.fromClose} />
            </p>
            <p className="g-timeline-item__memo-body">{memo.body}</p>
          </div>
        );
      })}
      {/* «Ascolta» resta montato mentre si scrive l'appunto: l'ascolto non si ferma. */}
      {player || (canAddNotes && !writing) ? (
        <div className="g-timeline-item__tools">
          {player}
          {canAddNotes && !writing ? (
            <Button ref={addRef} variant="ghost" onClick={() => setWriting(true)}>
              <Icon name="note" />
              {t("timeline.memo.add")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {canAddNotes && writing ? (
        <NoteComposer
          className="g-timeline-item__memo-form"
          texts={{ label: "timeline.memo.label", submit: "timeline.memo.submit", saving: "timeline.memo.saving", required: "timeline.memo.required" }}
          focusOnOpen
          onSave={saveMemo}
          onCancel={closeMemo}
        />
      ) : null}
    </Entry>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Nota

function NoteEntry({ item, tab, timeZone }: { item: NoteItem; tab: TabContext; timeZone: string }) {
  const t = useT();
  return (
    <Entry kind="note" icon="note" anchor={timelineAnchorId("note", item.id)}>
      <p className="g-timeline-item__title">{item.originLabel ? t("timeline.note.withOrigin", { origin: item.originLabel }) : t("timeline.note.title")}</p>
      <Meta at={item.occurredAt} known={item.occurredAtKnown} timeZone={timeZone} parts={[actorName(t, item.actor)]} fromClose={item.fromClose} />
      <p className="g-timeline-item__body">{item.body}</p>
      {item.attachments && item.attachments.length > 0 ? <NoteAttachments attachments={item.attachments} tab={tab} /> : null}
    </Entry>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Salvataggio: una o più modifiche dello stesso istante, con lo stesso autore

/** Le etichette dei campi di sistema che i cataloghi conoscono: il registro le scrive come chiavi. */
const SYSTEM_FIELD_KEYS: ReadonlySet<string> = new Set(messageKeysUnder("leadCard.fields"));

function isSystemFieldKey(label: string): label is Extract<MessageKey, `leadCard.fields.${string}`> {
  return SYSTEM_FIELD_KEYS.has(label);
}

/** Il campo di un cambio che non lo scrive: lo storico di Close registra il cambio di status senza etichetta. */
const LABELS_BY_TYPE: Readonly<Record<string, MessageKey>> = {
  "lead.status_changed": "leadCard.fields.status",
  "lead.owner_changed": "leadCard.fields.owner",
  "lead.tag_added": "leadCard.fields.tags",
  "lead.tag_removed": "leadCard.fields.tags",
  "lead.second_holder_removed": "leadCard.fields.secondHolder",
};

/**
 * Il nome del campo: l'etichetta dei cataloghi o quella del campo dell'azienda; senza, quella del tipo di cambio; mai il
 * riferimento tecnico (`system:…`) né una chiave che i cataloghi non conoscono, che diventano «Dato del Lead».
 */
function fieldLabel(t: Translate, change: Change): string {
  const label = change.fieldLabel;
  if (label !== null && isSystemFieldKey(label)) return t(label);
  if (label !== null && label.trim() !== "" && !label.startsWith("leadCard.")) return label;
  const byType = LABELS_BY_TYPE[change.type];
  return byType ? t(byType) : t("timeline.change.unknownField");
}

type ValueContext = { t: Translate; locale: Locale; fieldTypes: ReadonlyMap<string, string> };

/** Un valore di prima o di dopo, come lo scrive la scheda: testo, numero o euro, voce, città, status, owner, tag, telefono. */
function changeValue(ctx: ValueContext, change: Change, value: unknown): string {
  const { t, locale, fieldTypes } = ctx;
  if (value === null || value === undefined || value === "") return change.type === "lead.owner_changed" ? t("leadCard.noOwner") : t("timeline.change.empty");
  if (typeof value === "string") return change.type === "lead.phone_changed" ? formatPhoneForDisplay(value) : value;
  if (typeof value === "number") return fieldTypes.get(change.fieldRef ?? "") === "currency_eur" ? formatEuro(locale, value) : formatNumber(locale, value);
  if (typeof value === "boolean") return value ? t("timeline.change.yes") : t("timeline.change.no");
  if (Array.isArray(value)) return value.map((part) => changeValue(ctx, change, part)).join(", ");
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    // Una voce o una città (`label`), uno status o un tag (`name`), un owner (`displayName`).
    for (const key of ["label", "name", "displayName"]) {
      const text = record[key];
      if (typeof text === "string" && text.trim() !== "") return text;
    }
    // Il secondo titolare tolto: il nome, altrimenti l'email o il cellulare.
    const person = [record.firstName, record.lastName].filter((part): part is string => typeof part === "string" && part.trim() !== "").join(" ");
    if (person !== "") return person;
    if (typeof record.email === "string" && record.email !== "") return record.email;
    if (typeof record.mobile === "string" && record.mobile !== "") return formatPhoneForDisplay(record.mobile);
  }
  return "—";
}

/** La modifica di un campo, senza il nome del campo: «da «a» a «b»», «aggiunto «Academy»», «tolto «Academy»». */
function changeText(ctx: ValueContext, change: Change): string {
  const { t } = ctx;
  switch (change.type) {
    case "lead.tag_added":
      return t("timeline.change.added", { value: changeValue(ctx, change, change.after) });
    case "lead.tag_removed":
    case "lead.second_holder_removed":
      return t("timeline.change.removed", { value: changeValue(ctx, change, change.before) });
    default:
      return t("timeline.change.fromTo", { before: changeValue(ctx, change, change.before), after: changeValue(ctx, change, change.after) });
  }
}

function changeIcon(item: ChangeItem): IconName {
  if (item.changes.some((change) => change.type === "lead.owner_changed")) return "user";
  if (item.changes.every((change) => change.type === "lead.tag_added" || change.type === "lead.tag_removed")) return "tag";
  return "note";
}

function ChangeEntry({ item, timeZone, fieldTypes }: { item: ChangeItem; timeZone: string; fieldTypes: ReadonlyMap<string, string> }) {
  const t = useT();
  const { locale } = useLocale();
  const ctx: ValueContext = { t, locale, fieldTypes };
  const lines = item.changes.map((change) => ({ label: fieldLabel(t, change), text: changeText(ctx, change), reason: change.reason }));
  const meta = <Meta at={item.occurredAt} timeZone={timeZone} parts={[actorName(t, item.actor)]} fromClose={item.fromClose} />;
  const [only] = lines;

  if (lines.length === 1 && only) {
    return (
      <Entry kind="change" icon={changeIcon(item)} anchor={timelineAnchorId("change", item.id)}>
        <p className="g-timeline-item__title">{t("timeline.change.one", { field: only.label })}</p>
        {meta}
        <p className="g-timeline-item__body">{sentence(locale, only.text)}</p>
        {only.reason ? <p className="g-timeline-item__body">{t("timeline.change.reason", { reason: only.reason })}</p> : null}
      </Entry>
    );
  }

  const fields = [...new Set(lines.map((line) => line.label))].join(", ");
  return (
    <Entry kind="change" icon={changeIcon(item)} anchor={timelineAnchorId("change", item.id)}>
      <details className="g-timeline-item__group">
        <summary>
          <span className="g-timeline-item__title">{t("timeline.change.many", { count: lines.length, fields })}</span>
        </summary>
        <ul className="g-timeline-item__changes">
          {lines.map((line, index) => (
            <li key={index}>
              <span>{t("timeline.change.line", { field: line.label, change: line.text })}</span>
              {line.reason ? <span className="g-timeline-item__reason">{t("timeline.change.reason", { reason: line.reason })}</span> : null}
            </li>
          ))}
        </ul>
      </details>
      {meta}
    </Entry>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Gli altri eventi del registro: creazione, importazione e lo storico di Close

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value : null);
/** Disco o demo, da un evento; altrimenti `null`. */
const appointmentKindOf = (value: unknown): "disco" | "demo" | null => (value === "disco" || value === "demo" ? value : null);
const finiteNumber = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
const record = (value: unknown): Record<string, unknown> | null => (typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null);

/** Un importo nella sua valuta; una valuta che il browser non conosce resta scritta accanto al numero. */
function formatMoney(locale: Locale, value: number, currency: string | null): string {
  if (currency === null || currency === "EUR") return formatEuro(locale, value);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, useGrouping: "always" }).format(value);
  } catch {
    return `${formatNumber(locale, value)} ${currency}`;
  }
}

/** Una data di calendario di Close («2025-03-10») come «10/03/2025»; un istante nel fuso dell'azienda. */
function formatCloseDate(value: string, timeZone: string): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  return Number.isNaN(Date.parse(value)) ? value : formatNumericDateTime(timeZone, value);
}

/** Il valore di un campo di un'attività personalizzata di Close: testo, numero, sì/no o elenco. */
function customValue(t: Translate, locale: Locale, value: unknown): string {
  if (typeof value === "number") return formatNumber(locale, value);
  if (typeof value === "boolean") return value ? t("timeline.change.yes") : t("timeline.change.no");
  if (Array.isArray(value)) return value.map((part) => customValue(t, locale, part)).join(", ");
  return typeof value === "string" ? value : "—";
}

/** Il campo della scheda del posto di un telefono (`holder:kind`), come lo scrivono gli eventi dei telefoni. */
const PHONE_FIELDS: Readonly<Record<string, MessageKey>> = {
  "primary:mobile": "leadCard.fields.mobile",
  "primary:landline": "leadCard.fields.landline",
  "second:mobile": "leadCard.fields.secondHolderMobile",
};

const MEETING_STATUSES: Readonly<Record<string, MessageKey>> = {
  completed: "timeline.event.meeting.statuses.completed",
  canceled: "timeline.event.meeting.statuses.canceled",
  upcoming: "timeline.event.meeting.statuses.upcoming",
};

/** Gli stati di un'email di Close che dicono che non è partita; mandata e ricevuta non hanno una riga. */
// I canali dei messaggi entrati da GoHighLevel (INGR-30).
type GhlChannelKey = "email" | "whatsapp" | "instagram" | "facebook" | "sms" | "call";
const GHL_CHANNELS: ReadonlySet<string> = new Set<GhlChannelKey>(["email", "whatsapp", "instagram", "facebook", "sms", "call"]);

const CLOSE_EMAIL_STATUSES: Readonly<Record<string, MessageKey>> = {
  draft: "timeline.event.closeEmail.statuses.draft",
  scheduled: "timeline.event.closeEmail.statuses.scheduled",
  outbox: "timeline.event.closeEmail.statuses.outbox",
  error: "timeline.event.closeEmail.statuses.error",
};

type EventView = { icon: IconName; title: string; lines: string[]; body: string | null };

/** Il titolo di un Lead entrato da una porta, per il sistema dell'evento `lead.created`; «Nuovo Lead» resta «Lead creato». */
const ENTERED_TITLES: Readonly<Record<string, MessageKey>> = {
  web: "timeline.intake.enteredWeb",
  ghl: "timeline.intake.enteredGhl",
  meta: "timeline.intake.enteredMeta",
  file: "timeline.intake.enteredFile",
};

const REENTRY_SOURCES: Readonly<Record<string, MessageKey>> = {
  web: "timeline.intake.sources.web",
  ghl: "timeline.intake.sources.ghl",
  meta: "timeline.intake.sources.meta",
  file: "timeline.intake.sources.file",
  manual: "timeline.intake.sources.manual",
};

/** «Rientrato con lo stesso cellulare · da GoHighLevel, dati aggiornati, 2 campi e un tag», come nel mock-up della scheda. */
function reenteredTitle(t: Translate, payload: Record<string, unknown>): string {
  const source = t(REENTRY_SOURCES[text(payload.system) ?? ""] ?? "timeline.intake.sources.other");
  const fields = Array.isArray(payload.changedFields) ? payload.changedFields.length : 0;
  const tags = Array.isArray(payload.addedTags) ? payload.addedTags.length : 0;
  const parts: string[] = [];
  if (fields > 0) parts.push(fields === 1 ? t("timeline.intake.fieldsOne") : t("timeline.intake.fieldsMany", { count: fields }));
  if (tags > 0) parts.push(tags === 1 ? t("timeline.intake.tagsOne") : t("timeline.intake.tagsMany", { count: tags }));
  const [first, second] = parts;
  const changes =
    first === undefined ? t("timeline.intake.nothingNew") : t("timeline.intake.updated", { list: second === undefined ? first : t("timeline.intake.and", { first, second }) });
  return t("timeline.intake.reentered", { source, changes });
}

function eventView(t: Translate, locale: Locale, timeZone: string, item: EventItem): EventView {
  const payload = item.payload;
  // La sequenza di chiamate (piano M5, Task 12; D18): partita, ripartita, tentativo chiuso, non rispettata, finita.
  const sequence = sequenceEventTitle(t, item.type, payload);
  if (sequence) return { ...sequence, lines: [], body: null };
  switch (item.type) {
    case "lead.created": {
      const status = text(record(payload.status)?.name);
      const ownerName = text(record(payload.owner)?.displayName);
      const lines = status === null ? [] : [t("timeline.event.createdDetails", { status, owner: ownerName ?? t("leadCard.noOwner") })];
      // Un Lead entrato da una porta (piano dell'ingresso del 10 ottobre 2026, I2): «Entrato dal sito», «Entrato da GoHighLevel»…
      const entered = ENTERED_TITLES[text(payload.system) ?? ""];
      const title = item.fromClose ? t("timeline.event.createdInClose") : entered ? t(entered) : t("timeline.event.created");
      return { icon: entered ? "down" : "plus", title, lines, body: null };
    }
    // Un Lead che rientra con lo stesso cellulare (piano dell'ingresso, I1 e I2; INGR-6): da dove e che cosa è cambiato; le modifiche
    // stesse sono l'elemento dei campi e dei tag dello stesso salvataggio.
    case "lead.reentered":
      return { icon: "down", title: reenteredTitle(t, payload), lines: [], body: null };
    case "lead.imported":
      return { icon: "down", title: t(payload.joinedByMobile === true ? "timeline.event.joinedFromClose" : "timeline.event.imported"), lines: [], body: null };
    // «Archivia» e «Ripristina» (piano M3 Task 12): il motivo dell'archiviazione arriva con l'evento, se è stato scritto.
    case "lead.archived": {
      const reason = text(item.reason);
      return { icon: "trash", title: t("timeline.event.archived"), lines: reason === null ? [] : [t("timeline.event.archivedReason", { reason })], body: null };
    }
    case "lead.restored":
      return { icon: "lead", title: t("timeline.event.restored"), lines: [], body: null };
    // «Non inviare WhatsApp» acceso e spento (piano M6, R1, Task 4b; D25): il titolo e il testo del mock-up.
    case "lead.whatsapp_opted_out":
      return { icon: "whatsapp", title: t("timeline.event.whatsappOptedOut"), lines: [], body: t("timeline.event.whatsappOptedOutText") };
    case "lead.whatsapp_opted_in":
      return { icon: "whatsapp", title: t("timeline.event.whatsappOptedIn"), lines: [], body: t("timeline.event.whatsappOptedInText") };
    // «Unisci in un solo Lead» (piano M3 Task 18): sul Lead che resta chi è entrato e il motivo della coppia (le chiamate, le
    // note e le modifiche dell'altro Lead sono ora in questa Timeline, ognuna alla sua data); sul Lead assorbito in chi è
    // entrato. Senza nome, «un altro Lead».
    case "lead.merged": {
      const name = text(payload.absorbedDisplayName);
      const value = text(payload.value);
      const lines: string[] = [];
      if (value !== null && payload.reason === "phone") lines.push(t("dataCleanup.samePhone", { value: formatPhoneForDisplay(value) }));
      else if (value !== null && payload.reason === "email") lines.push(t("dataCleanup.sameEmail", { value }));
      lines.push(t("timeline.event.mergedDetails"));
      return { icon: "lead", title: name === null ? t("timeline.event.mergedNoName") : t("timeline.event.merged", { name }), lines, body: null };
    }
    case "lead.merged_away": {
      const name = text(payload.survivorDisplayName);
      return { icon: "lead", title: name === null ? t("timeline.event.mergedAwayNoName") : t("timeline.event.mergedAway", { name }), lines: [], body: null };
    }
    // L'esito arrivato quando la chiamata ne aveva già uno (piano M4, D7): i valori proposti, non applicati, e i suoi appunti.
    case "call.outcome_late": {
      const outcome = text(payload.outcome);
      const lines = outcome === null ? [] : [t("timeline.event.outcomeLateProposed", { outcome: outcomeText(t, outcome, text(payload.answeredOutcome), text(payload.detail)) })];
      return { icon: "clock", title: t("timeline.event.outcomeLate"), lines, body: text(payload.note) };
    }
    // «Numero errato» (piano M4, D7): il telefono chiamato, con il suo posto e il numero, come nel mock-up.
    case "lead.phone_marked_wrong": {
      const fieldKey = PHONE_FIELDS[`${text(payload.holder) ?? ""}:${text(payload.kind) ?? ""}`];
      const e164 = text(payload.e164);
      const number = e164 === null ? "—" : formatPhoneForDisplay(e164);
      const title = fieldKey
        ? t("timeline.event.phoneMarkedWrong", { field: t(fieldKey).toLocaleLowerCase(locale), number })
        : t("timeline.event.phoneMarkedWrongNumber", { number });
      return { icon: "phone", title, lines: [], body: null };
    }
    // Il Power Dialer (piano M5, Task 6; D5 e D18): «Salta» con il motivo delle quattro voci del mock-up (uno sconosciuto vale
    // «altro motivo») e la nota, oppure «Lead successivo» senza aver chiamato il Lead.
    case "task.created": {
      const due = text(payload.dueAt);
      if (due === null) return { icon: "clock", title: t("timeline.event.other"), lines: [], body: null };
      const when = formatNumericDateTime(timeZone, due);
      return { icon: "clock", title: t(payload.agreedWithLead === true ? "timeline.event.taskCreatedAgreed" : "timeline.event.taskCreated", { when }), lines: [], body: null };
    }
    case "task.rescheduled": {
      const before = text(record(item.before)?.dueAt);
      const after = text(record(item.after)?.dueAt);
      if (before === null || after === null) return { icon: "clock", title: t("timeline.event.other"), lines: [], body: null };
      // Lo stesso orario: «Sposta» ha cambiato soltanto «Concordato con il Lead».
      if (Date.parse(before) === Date.parse(after)) {
        const agreed = record(item.after)?.agreedWithLead === true;
        return { icon: "clock", title: t(agreed ? "timeline.event.taskAgreed" : "timeline.event.taskNotAgreed", { when: formatNumericDateTime(timeZone, after) }), lines: [], body: null };
      }
      return {
        icon: "clock",
        title: t("timeline.event.taskRescheduled", { before: formatNumericDateTime(timeZone, before), after: formatNumericDateTime(timeZone, after) }),
        lines: [],
        body: null,
      };
    }
    case "task.completed":
      return { icon: "check", title: t("timeline.event.taskCompleted"), lines: [], body: null };
    case "task.canceled": {
      // Archivio e unione annullano anche disco e demo (R7): il loro nome, non «Richiamo».
      const kind = appointmentKindOf(payload.taskType);
      if (kind !== null) return { icon: "close", title: t("appointments.timeline.canceled", { kind: t(`appointments.kind.${kind}`) }), lines: [], body: null };
      return { icon: "close", title: t("timeline.event.taskCanceled"), lines: [], body: null };
    }
    // Disco e demo (piano M6, R7, Task 6): prenotata, spostata, annullata con il motivo, fatta, il Lead non si è presentato.
    case "appointment.booked":
    case "appointment.rescheduled":
    case "appointment.canceled":
    case "appointment.held":
    case "appointment.no_show": {
      const kind = appointmentKindOf(payload.kind);
      const startsAt = text(payload.startsAt);
      if (kind === null || startsAt === null) return { icon: "calendar", title: t("timeline.event.other"), lines: [], body: null };
      const name = t(`appointments.kind.${kind}`);
      const when = formatNumericDateTime(timeZone, startsAt);
      const minutes = finiteNumber(payload.minutes) ?? 0;
      if (item.type === "appointment.booked") return { icon: "calendar", title: t("appointments.timeline.booked", { kind: name, when, minutes }), lines: [], body: null };
      if (item.type === "appointment.rescheduled") return { icon: "calendar", title: t("appointments.timeline.rescheduled", { kind: name, when }), lines: [], body: null };
      if (item.type === "appointment.held") return { icon: "check", title: t("appointments.timeline.held", { kind: name }), lines: [], body: null };
      if (item.type === "appointment.no_show") return { icon: "close", title: t("appointments.timeline.noShow", { kind: name }), lines: [], body: null };
      const reason = text(payload.reason);
      return { icon: "close", title: t("appointments.timeline.canceled", { kind: name }), lines: reason === null ? [] : [t("appointments.timeline.canceledReason", { reason })], body: null };
    }
    case "opportunity.opened":
    case "opportunity.stage_changed": {
      const stage = text(payload.stage);
      if (stage === null) return { icon: "report", title: t("timeline.event.other"), lines: [], body: null };
      const before = text(payload.before);
      if (item.type === "opportunity.opened") return { icon: "report", title: t("appointments.timeline.opportunityOpened", { stage: stageText(t, stage) }), lines: [], body: null };
      return {
        icon: "report",
        title: before === null ? t("appointments.timeline.stageNow", { stage: stageText(t, stage) }) : t("appointments.timeline.stageChanged", { before: stageText(t, before), after: stageText(t, stage) }),
        lines: [],
        body: null,
      };
    }
    // «Venduto» e «Persa» dalla scheda (piano M6, R8, Task 25), come le scrive il mock-up: che cosa e per quanto, chi ha chiuso; il motivo.
    case "opportunity.sold": {
      const product = payload.product === "club" || payload.product === "percorso" ? t(`opportunities.products.${payload.product}`) : null;
      const cents = finiteNumber(payload.valueCents);
      if (product === null || cents === null) return { icon: "check", title: t("timeline.event.other"), lines: [], body: null };
      const closer = text(payload.closedByDisplayName);
      return {
        icon: "check",
        title: t("opportunities.timeline.sold", { product, value: formatEuroCents(locale, cents) }),
        lines: closer === null ? [] : [t("opportunities.timeline.closedBy", { name: closer })],
        body: null,
      };
    }
    // Un'opportunità entrata o aggiornata dall'importazione da Close (INGR-26): la fase di adesso (con quella di prima) e il valore.
    case "opportunity.imported": {
      const stage = text(payload.stage);
      if (stage === null) return { icon: "report", title: t("timeline.event.other"), lines: [], body: null };
      const before = text(payload.before);
      const cents = finiteNumber(payload.valueCents);
      return {
        icon: "report",
        title: before === null || before === stage ? t("opportunities.timeline.imported", { stage: stageText(t, stage) }) : t("opportunities.timeline.importedChanged", { before: stageText(t, before), after: stageText(t, stage) }),
        lines: cents === null ? [] : [t("opportunities.timeline.importedValue", { value: formatEuroCents(locale, cents) })],
        body: null,
      };
    }
    case "opportunity.lost": {
      const reason = OPPORTUNITY_LOST_REASONS.find((candidate) => candidate === payload.reason);
      const note = text(payload.note);
      const lines = reason === undefined ? [] : [t("opportunities.timeline.reason", { reason: lostReasonText(t, { reason, note }) })];
      return { icon: "close", title: t("opportunities.timeline.lost"), lines, body: null };
    }
    case "dialer.lead_skipped": {
      if (payload.reason === "passed_without_call") return { icon: "skip", title: t("timeline.event.dialerPassed"), lines: [], body: null };
      return { icon: "skip", title: t("timeline.event.dialerSkipped", { reason: skipReasonText(t, text(payload.reason)) }), lines: [], body: text(payload.note) };
    }
    case "close.opportunity_status_changed": {
      const before = text(payload.before);
      const after = text(payload.after) ?? "—";
      const pipeline = text(payload.pipeline);
      const value = finiteNumber(payload.value);
      const lines = [before === null ? t("timeline.event.opportunity.phase", { after }) : t("timeline.event.opportunity.phases", { before, after })];
      if (pipeline !== null) lines.push(t("timeline.event.opportunity.pipeline", { name: pipeline }));
      if (value !== null) {
        const money = formatMoney(locale, value, text(payload.currency));
        const period = text(payload.valuePeriod);
        const amount = period === "monthly" ? t("timeline.event.opportunity.monthly", { value: money }) : period === "annual" ? t("timeline.event.opportunity.annual", { value: money }) : money;
        lines.push(t("timeline.event.opportunity.value", { value: amount }));
      }
      return { icon: "report", title: t("timeline.event.opportunity.title"), lines, body: null };
    }
    case "close.task_completed": {
      const due = text(payload.dueDate);
      const assignedTo = text(payload.assignedTo);
      const lines: string[] = [];
      if (due !== null) lines.push(t("timeline.event.task.due", { date: formatCloseDate(due, timeZone) }));
      if (assignedTo !== null) lines.push(t("timeline.event.task.assignedTo", { name: assignedTo }));
      return { icon: "check", title: t("timeline.event.task.title"), lines, body: text(payload.text) };
    }
    case "close.meeting": {
      const title = text(payload.title);
      const startsAt = text(payload.startsAt);
      const duration = finiteNumber(payload.durationSeconds);
      const attendees = Array.isArray(payload.attendees) ? payload.attendees.filter((name): name is string => typeof name === "string" && name.trim() !== "") : [];
      const location = text(payload.location);
      const statusKey = MEETING_STATUSES[text(payload.status) ?? ""];
      const lines: string[] = [];
      if (startsAt !== null) lines.push(t("timeline.event.meeting.start", { when: formatCloseDate(startsAt, timeZone) }));
      if (duration !== null) lines.push(t("timeline.event.meeting.duration", { duration: formatClockDuration(duration) }));
      if (attendees.length > 0) lines.push(t("timeline.event.meeting.attendees", { names: attendees.join(", ") }));
      if (location !== null) lines.push(t("timeline.event.meeting.location", { place: location }));
      if (statusKey) lines.push(t("timeline.event.meeting.status", { status: t(statusKey) }));
      return { icon: "calendar", title: title === null ? t("timeline.event.meeting.title") : t("timeline.event.meeting.titled", { title }), lines, body: text(payload.note) };
    }
    // Un'email di Close entrata come storia (22/10/2026): direzione, oggetto, stato se non è partita, testo; mai un invio.
    case "close.email": {
      const direction = text(payload.direction);
      const subject = text(payload.subject);
      const statusKey = CLOSE_EMAIL_STATUSES[text(payload.status) ?? ""];
      const attachments = finiteNumber(payload.attachments) ?? 0;
      const lines = [subject === null ? t("timeline.event.closeEmail.noSubject") : t("timeline.event.closeEmail.subject", { subject })];
      if (statusKey) lines.push(t("timeline.event.closeEmail.status", { status: t(statusKey) }));
      if (attachments > 0) lines.push(t("timeline.event.closeEmail.attachments", { count: attachments }));
      if (payload.bodyTruncated === true) lines.push(t("timeline.event.closeEmail.truncated"));
      const title = direction === "outgoing" ? t("timeline.event.closeEmail.sent") : direction === "incoming" ? t("timeline.event.closeEmail.received") : t("timeline.event.closeEmail.other");
      return { icon: "mail", title, lines, body: text(payload.body) };
    }
    // Un messaggio di GoHighLevel entrato come storia (INGR-30): canale, direzione, delle email l'oggetto, il testo; mai un invio.
    case "ghl.message": {
      const channel = GHL_CHANNELS.has(text(payload.channel) ?? "") ? (text(payload.channel) as GhlChannelKey) : "email";
      const label = t(`timeline.event.ghlMessage.channels.${channel}`);
      const direction = text(payload.direction);
      const lines: string[] = [];
      if (channel === "email") {
        const subject = text(payload.subject);
        lines.push(subject === null ? t("timeline.event.ghlMessage.noSubject") : t("timeline.event.ghlMessage.subject", { subject }));
      }
      const duration = finiteNumber(payload.durationSeconds);
      if (channel === "call" && duration !== null) lines.push(t("timeline.event.ghlMessage.duration", { minutes: Math.floor(duration / 60), seconds: duration % 60 }));
      if (payload.bodyTruncated === true) lines.push(t("timeline.event.ghlMessage.truncated"));
      const title = direction === "inbound" ? t("timeline.event.ghlMessage.inbound", { channel: label }) : direction === "outbound" ? t("timeline.event.ghlMessage.outbound", { channel: label }) : t("timeline.event.ghlMessage.other", { channel: label });
      return { icon: channel === "whatsapp" ? "whatsapp" : channel === "call" ? "phone" : channel === "email" ? "mail" : "conversations", title, lines, body: text(payload.body) };
    }
    // Le newsletter e le sequenze di GoHighLevel di un canale in una riga sola, col conteggio e il periodo (INGR-30).
    case "ghl.automated_messages": {
      const channel = GHL_CHANNELS.has(text(payload.channel) ?? "") ? (text(payload.channel) as GhlChannelKey) : "email";
      // Il periodo è di tutte le automatiche del canale fino a quel giorno: il titolo dice il loro totale, la riga quante sono entrate qui.
      const count = finiteNumber(payload.count) ?? 0;
      const total = finiteNumber(payload.total) ?? count;
      const from = text(payload.firstAt);
      const to = text(payload.lastAt);
      const lines = from !== null && to !== null ? [t("timeline.event.ghlMessage.automatedPeriod", { from: formatNumericDateTime(timeZone, from), to: formatNumericDateTime(timeZone, to) })] : [];
      if (total > count) lines.push(t("timeline.event.ghlMessage.automatedNew", { count, total }));
      return {
        icon: channel === "email" ? "mail" : "conversations",
        title: t("timeline.event.ghlMessage.automated", { channel: t(`timeline.event.ghlMessage.channels.${channel}`), count: total }),
        lines,
        body: null,
      };
    }
    // Una disco o una demo storica di Close entrata come appuntamento già chiuso (22/10/2026): prenotata, per quando, com'è andata.
    case "appointment.imported": {
      const kind = appointmentKindOf(payload.kind);
      const dueAt = text(payload.dueAt);
      if (kind === null || dueAt === null) return { icon: "calendar", title: t("timeline.event.other"), lines: [], body: null };
      const outcome = payload.outcome === "held" ? "held" : payload.outcome === "canceled" ? "canceled" : "unknown";
      return {
        icon: "calendar",
        title: t("appointments.timeline.imported", { kind: t(`appointments.kind.${kind}`) }),
        lines: [t("appointments.timeline.importedWhen", { when: formatNumericDateTime(timeZone, dueAt) }), t(`appointments.timeline.importedOutcome.${outcome}`)],
        body: null,
      };
    }
    // Fatti in Close e soltanto raccontati (22/10/2026): senza i dati dell'altro Lead né quelli cambiati.
    case "close.import_update":
      return { icon: "info", title: t("timeline.event.closeImportUpdate"), lines: [], body: null };
    case "close.lead_merge":
      return { icon: "info", title: t("timeline.event.closeLeadMerge"), lines: [], body: null };
    case "close.custom_activity": {
      const fields = Array.isArray(payload.fields) ? payload.fields.map(record).filter((field): field is Record<string, unknown> => field !== null) : [];
      const lines = fields.map((field) =>
        t("timeline.event.custom.field", { name: text(field.name) ?? t("timeline.event.custom.unnamedField"), value: customValue(t, locale, field.value) }),
      );
      return { icon: "info", title: text(payload.typeName) ?? t("timeline.event.custom.title"), lines, body: null };
    }
    default:
      return { icon: "info", title: t("timeline.event.other"), lines: [], body: null };
  }
}

/** Il titolo e l'icona di un evento del registro come li scrive la Timeline: li usa anche «Attività» (SEZ-8), con le stesse parole. */
export function timelineEventTitle(t: Translate, locale: Locale, timeZone: string, item: EventItem): { icon: IconName; title: string } {
  const view = eventView(t, locale, timeZone, item);
  return { icon: view.icon, title: view.title };
}

/** Il nome del campo di una modifica, come lo scrive la Timeline («Email», «Status», «Dato del Lead»): anche per «Attività». */
export function timelineFieldLabel(t: Translate, change: Change): string {
  return fieldLabel(t, change);
}

/** Una modifica in una riga, come la scrive la Timeline («Status: da «Da chiamare» a «Interessato»»): anche per «Attività». */
export function timelineChangeLine(t: Translate, locale: Locale, change: Change): string {
  return t("timeline.change.line", { field: fieldLabel(t, change), change: changeText({ t, locale, fieldTypes: new Map() }, change) });
}

function EventEntry({ item, timeZone }: { item: EventItem; timeZone: string }) {
  const t = useT();
  const { locale } = useLocale();
  const view = eventView(t, locale, timeZone, item);
  return (
    <Entry kind="event" icon={view.icon} anchor={timelineAnchorId("event", item.id)}>
      <p className="g-timeline-item__title">{view.title}</p>
      <Meta at={item.occurredAt} timeZone={timeZone} parts={[actorName(t, item.actor)]} fromClose={item.fromClose} />
      {view.lines.length > 0 ? (
        <ul className="g-timeline-item__details">
          {view.lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : null}
      {view.body ? <p className="g-timeline-item__body">{view.body}</p> : null}
    </Entry>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// WhatsApp

/** Lo stato di un messaggio del WhatsApp ufficiale, come lo dice Meta (piano M6, R2, Task 10a). */
const OFFICIAL_STATES: Partial<Record<WhatsAppItem["status"], Extract<MessageKey, `timeline.whatsapp.officialStates.${string}`>>> = {
  queued: "timeline.whatsapp.officialStates.queued",
  sent: "timeline.whatsapp.officialStates.sent",
  delivered: "timeline.whatsapp.officialStates.delivered",
  read: "timeline.whatsapp.officialStates.read",
  failed: "timeline.whatsapp.officialStates.failed",
};

/** Il codice di un messaggio preso in carico senza mai la risposta di Meta: «Non confermato» (questione 415). */
const NOT_CONFIRMED = "not_confirmed";

/** I codici di Meta con un testo nei cataloghi. */
const FAILURE_REASON_KEYS: ReadonlySet<string> = new Set(messageKeysUnder("timeline.whatsapp.failureReasons"));

function isFailureReasonKey(key: string): key is Extract<MessageKey, `timeline.whatsapp.failureReasons.${string}`> {
  return FAILURE_REASON_KEYS.has(key);
}

/**
 * Il titolo dallo stato di adesso, con il modello da cui nasce: «WhatsApp dichiarato inviato · modello «Primo contatto»». Da WhatsApp
 * Agile (piano M6, R9, Task 27b; WA-7) lo stato che Agile conosce: «Preso in carico da WhatsApp Agile», mai consegnato né letto.
 */
function whatsAppTitle(t: Translate, item: WhatsAppItem): string {
  if (item.direction === "in") return t("timeline.whatsapp.received");
  const official = item.channel === "meta" && item.direction === "out" ? OFFICIAL_STATES[item.status] : undefined;
  const agile = item.channel === "agile" ? agileStateKey(item.status, item.failureCode) : null;
  let title: string;
  if (item.status === "prepared") title = t("timeline.whatsapp.prepared");
  else if (item.status === "declared_sent") title = t("timeline.whatsapp.declaredSent");
  else if (agile !== null) title = t(agile);
  else if (official !== undefined) {
    const state = item.status === "failed" && item.failureCode === NOT_CONFIRMED ? t("timeline.whatsapp.officialStates.notConfirmed") : t(official);
    title = t("timeline.whatsapp.official", { state });
  } else title = t("timeline.whatsapp.other");
  return item.templateName ? t("timeline.whatsapp.withTemplate", { title, name: item.templateName }) : title;
}

/**
 * Il motivo di un messaggio non consegnato: il testo dei cataloghi per i codici più comuni di Meta (e per «Non confermato»),
 * altrimenti il titolo di Meta con il codice; `null` per gli altri stati o senza né codice né titolo.
 */
function whatsAppFailure(t: Translate, item: WhatsAppItem): string | null {
  if (item.status !== "failed") return null;
  if (item.channel === "agile") return agileFailureText(t, item.failureCode, item.failureReason);
  const { failureCode: code, failureReason: title } = item;
  const key = code === null ? null : `timeline.whatsapp.failureReasons.${code === NOT_CONFIRMED ? "notConfirmed" : `code${code.split(":")[0] ?? code}`}`;
  let reason: string | null;
  if (key !== null && isFailureReasonKey(key)) reason = t(key);
  else if (code !== null && title !== null) reason = t("timeline.whatsapp.failureWithCode", { title, code });
  else if (code !== null) reason = t("timeline.whatsapp.failureCode", { code });
  else reason = title;
  return reason === null ? null : t("timeline.whatsapp.failureReason", { reason });
}

function WhatsAppEntry({ item, tab, timeZone }: { item: WhatsAppItem; tab: TabContext; timeZone: string }) {
  const t = useT();
  const titleId = useId();
  const toast = useToast();
  const view = useLeadView();
  const declare = useDeclareWhatsAppSent(tab, view);
  const markRead = useMarkWhatsAppRead(tab, view);
  const declared = item.status === "declared_sent";
  const inbound = item.direction === "in";
  let readBy: string | null = null;
  if (inbound && item.readAt !== null) {
    const when = formatNumericDateTime(timeZone, item.readAt);
    readBy = item.readBy === null ? t("timeline.whatsapp.readByPlatform", { when }) : t("timeline.whatsapp.readBy", { name: item.readBy, when });
  }
  // Un messaggio del Lead non ha un autore della squadra: in «quando · chi» il numero dell'azienda che l'ha ricevuto e, letto, da chi.
  // Uno che Gioia365 manda da sé (la conferma dello «STOP», questione 420) è «Gioia365 (automatico)».
  const parts = inbound
    ? [t("timeline.whatsapp.to", { number: formatPhoneForDisplay(item.toE164) }), readBy]
    : [
        item.automatic !== null ? t("timeline.whatsapp.automaticAuthor") : actorName(t, item.actor),
        t(item.channel === "manual" ? "timeline.whatsapp.openedTo" : "timeline.whatsapp.to", { number: formatPhoneForDisplay(item.toE164) }),
        declared ? t("timeline.whatsapp.declaredAt", { when: formatNumericDateTime(timeZone, item.statusAt) }) : null,
        declared ? t("timeline.whatsapp.unverifiable") : null,
        whatsAppFailure(t, item),
      ];
  const unread =
    inbound && item.readAt === null ? (
      <StatusPill tone="warning" icon="clock">
        {t("timeline.whatsapp.unread")}
      </StatusPill>
    ) : null;
  const kind = inbound ? inboundKindKey(item.contentKind) : null;
  const canMarkRead = inbound && item.readAt === null && item.canMarkRead;

  function declareSent() {
    declare.mutateAsync(item.id).then(
      () => toast.show(t("whatsapp.dialog.sentDone"), "success"),
      (error: unknown) => {
        if (alreadyDeclared(error)) toast.show(t("whatsapp.dialog.sentDone"), "success");
        else toast.show(messageForError(t, error), "warning");
      },
    );
  }

  function markAsRead() {
    markRead.mutate({ messageId: item.id, leadId: null }, { onError: (error) => toast.show(messageForError(t, error), "warning") });
  }

  return (
    <Entry kind="whatsapp" icon="whatsapp" anchor={timelineAnchorId("whatsapp", item.id)}>
      <p id={titleId} className="g-timeline-item__title">
        {whatsAppTitle(t, item)}
      </p>
      <Meta at={item.occurredAt} known={item.occurredAtKnown} timeZone={timeZone} parts={parts} fromClose={item.fromClose} badge={unread} />
      {kind !== null ? <p className="g-timeline-item__body">{t(kind)}</p> : null}
      {kind === null || item.body !== "" ? <p className="g-timeline-item__body">{item.body}</p> : null}
      {item.canDeclareSent && !declared ? (
        <div className="g-timeline-item__tools">
          <Button variant="ghost" disabled={declare.isPending} aria-busy={declare.isPending ? "true" : undefined} aria-describedby={titleId} onClick={declareSent}>
            <Icon name="check" />
            {t("whatsapp.dialog.sent")}
          </Button>
        </div>
      ) : null}
      {canMarkRead ? (
        <div className="g-timeline-item__tools">
          <Button variant="ghost" disabled={markRead.isPending} aria-busy={markRead.isPending ? "true" : undefined} aria-describedby={titleId} onClick={markAsRead}>
            <Icon name="check" />
            {t("timeline.whatsapp.markRead")}
          </Button>
        </div>
      ) : null}
    </Entry>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Email

/** Un evento delle email di GoHighLevel (in cima al file): in sola lettura, senza azioni (PROD-3). */
function EmailEntry({ item, timeZone }: { item: EmailItem; timeZone: string }) {
  const t = useT();
  return (
    <Entry kind="email" icon="mail">
      <p className="g-timeline-item__title">{t(`timeline.email.events.${item.event}`)}</p>
      <Meta at={item.occurredAt} timeZone={timeZone} parts={[actorName(t, item.actor)]} fromClose={false} />
      {item.subject !== null ? <p className="g-timeline-item__body">{t("timeline.email.subject", { subject: item.subject })}</p> : null}
      {item.url !== null ? (
        <p className="g-timeline-item__body">
          {t("timeline.email.link")}{" "}
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            {item.url}
          </a>
        </p>
      ) : null}
    </Entry>
  );
}
